/**
 * ForecastPage — 8-day forecast screen (DASH-FC1 rebuild).
 *
 * Consumes the shipped `computePerDayTiers` engine + shared rating primitives.
 * NEAR/FAR treatment is engine-derived (via `isNearDay`) — never a hardcoded
 * day-index cutoff. Best-window card from `deriveBestWindow` (argmax of score).
 *
 * Pro gate + EVENT_FORECAST_PAGE_VIEWED mount tracking preserved unchanged.
 *
 * Scope fence (RULE-276): no engine edits, no StorageAdapter changes, no
 * ConditionsWidget/W1 edits, no .claude/** changes.
 */

import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';

import { usePropertyStore } from '@/stores/property-store';
import { useUserProfileStore } from '@/stores/user-profile-store';
import { UpsellSheet } from '@/components/common/UpsellSheet';
import { UPSELL_COPY } from '@/constants/upsell-copy';
import { getWeatherForProperty } from '@/services/weather-service';
import {
  hpaToInHg,
  windDegToCardinal,
  type WeatherData,
} from '@/services/weather-service';
import { getSunTimes, getMoonData } from '@/services/celestial-service';
import {
  computePerDayTiers,
  computeFavorabilityByPeriod,
} from '@/services/conditions-quality-service';
import { posthogService } from '@/services/posthog-service';
import { EVENT_FORECAST_PAGE_VIEWED } from '@/services/observability/events';
import { SegmentedControl } from '@/components/common/SegmentedControl';
import { ConditionMeter } from '@/components/common/ConditionMeter';
import { CONDITION_RATING_DISPLAY } from '@/components/common/conditionRatingDisplay';
import { ForecastDayRow } from '@/components/forecast/ForecastDayRow';
import {
  deriveBestWindow,
  isNearDay,
} from '@/pages/forecast/forecastDerivation';
import type { PerDayTier, FavorabilityPoint, QualityLevel } from '@/types/conditions';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Day label: "Today" for index 0, else short weekday + date number. */
function formatDayLabel(dtUnix: number, index: number): string {
  if (index === 0) return 'Today';
  const date = new Date(dtUnix * 1000);
  const day = date.toLocaleDateString(undefined, { weekday: 'short' });
  const dayNum = date.getDate();
  return `${day} ${dayNum}`;
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function getPressureTrendWord(hpa: number, prevHpa: number | undefined): string {
  if (prevHpa === undefined) return 'steady';
  const delta = hpa - prevHpa;
  if (delta >= 1) return 'rising';
  if (delta <= -1) return 'falling';
  return 'steady';
}

// ---------------------------------------------------------------------------
// Per-row derived data builder
// ---------------------------------------------------------------------------

interface RowDerivedData {
  dayLabel: string;
  level: QualityLevel;
  score: number;
  isNear: boolean;
  tempMax: number;
  tempMin: number;
  precipPct: number;
  windSpeed: number;
  windDir: string;
  moonIllumination: number;
  moonPhaseValue: number;
  civilDawnTime: string;
  civilDuskTime: string;
  pressureInHg: number;
  pressureTrend: string;
  precipitationVolume?: number;
  sparklinePoints: FavorabilityPoint[];
  dayDate: Date;
}

function buildRowData(
  daily: WeatherData['daily'][number],
  index: number,
  allDaily: WeatherData['daily'],
  weather: WeatherData,
  lat: number,
  lng: number,
  tier: PerDayTier,
): RowDerivedData {
  const dayDate = new Date(daily.dt * 1000);
  const sunTimes = getSunTimes(dayDate, lat, lng);
  const moonData = getMoonData(dayDate, lat, lng);
  const pressureInHg = hpaToInHg(daily.pressure);
  const prevHpa = index > 0 ? allDaily[index - 1].pressure : undefined;
  const pressureTrend = getPressureTrendWord(daily.pressure, prevHpa);
  const windDir = windDegToCardinal(daily.windDeg);
  const precipPct = Math.round((daily.pop ?? 0) * 100);
  const precipitationVolume =
    Math.round(((daily.rain ?? 0) + (daily.snow ?? 0)) / 25.4 * 100) / 100 || undefined;

  const near = isNearDay(weather, dayDate, lat, lng);
  const sparklinePoints = near
    ? computeFavorabilityByPeriod(weather, dayDate, lat, lng)
    : [];

  return {
    dayLabel: formatDayLabel(daily.dt, index),
    level: tier.tier,
    score: tier.score,
    isNear: near,
    tempMax: daily.tempMax,
    tempMin: daily.tempMin,
    precipPct,
    windSpeed: daily.windSpeed,
    windDir,
    moonIllumination: moonData.illumination,
    moonPhaseValue: moonData.phaseValue,
    civilDawnTime: formatTime(sunTimes.civilDawn),
    civilDuskTime: formatTime(sunTimes.civilDusk),
    pressureInHg,
    pressureTrend,
    precipitationVolume,
    sparklinePoints,
    dayDate,
  };
}

// ---------------------------------------------------------------------------
// ForecastPage
// ---------------------------------------------------------------------------

export function ForecastPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { properties, loadProperties } = usePropertyStore();
  const isPro = useUserProfileStore((s) => s.isPro);

  const [selectedId, setSelectedId] = useState<string>(() => searchParams.get('propertyId') ?? '');
  const [rawWeather, setRawWeather] = useState<WeatherData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);

  // Ref map for scrolling to a row when the best-window card is tapped.
  const rowRefs = useRef<Record<number, HTMLElement | null>>({});

  // Track forecast page view once on mount — only for Pro users.
  useEffect(() => {
    if (isPro()) {
      posthogService.track(EVENT_FORECAST_PAGE_VIEWED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (properties.length === 0) {
      void loadProperties();
    }
  }, [properties.length, loadProperties]);

  // Default to first property once loaded.
  useEffect(() => {
    if (!selectedId && properties.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot sync from external prop on mount
      setSelectedId(properties[0].id);
    }
  }, [selectedId, properties]);

  const property = properties.find((p) => p.id === selectedId) ?? properties[0];

  useEffect(() => {
    if (!property) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- loading flag set before async fetch; reset in cleanup
    setIsLoading(true);
    getWeatherForProperty(property.id, property.latitude, property.longitude)
      .then(setRawWeather)
      .catch((err: unknown) => {
        console.error('[ForecastPage] Failed to load weather:', err);
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `property` is recomputed each render via properties.find(); depending on the object would refetch every render. Primitive id/lat/long deps are the intended trigger set.
  }, [property?.id, property?.latitude, property?.longitude]);

  // Gate: Starter users cannot access the 8-day forecast.
  if (!isPro()) {
    return (
      <UpsellSheet
        featureName={UPSELL_COPY.eight_day_forecast.featureName}
        description={UPSELL_COPY.eight_day_forecast.description}
        featureGate="eight_day_forecast"
        onDismiss={() => navigate(-1)}
      />
    );
  }

  function handleToggle(index: number) {
    setExpandedIndex((prev) => (prev === index ? null : index));
  }

  /** Best-window tap: scroll to the row and expand it. */
  function handleBestWindowTap(index: number) {
    setExpandedIndex(index);
    const el = rowRefs.current[index];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  // Build segmented control options for property selector.
  const segmentedOptions = properties.map((p) => ({ value: p.id, label: p.name }));

  // Derive per-day tiers from the engine.
  const tiers: PerDayTier[] =
    rawWeather && property
      ? computePerDayTiers(rawWeather, property.latitude, property.longitude)
      : [];

  // Best-window derivation (argmax of score, tiebreak earlier date).
  const bestTier = deriveBestWindow(tiers);

  // Build per-row derived data (align tiers with weather.daily by index).
  const rows: RowDerivedData[] =
    rawWeather && property
      ? tiers.map((tier, i) => {
          // CAST: tiers are capped at Math.min(daily.length, 8) — index i is always valid.
          const daily = rawWeather.daily[i];
          return buildRowData(
            daily,
            i,
            rawWeather.daily,
            rawWeather,
            property.latitude,
            property.longitude,
            tier,
          );
        })
      : [];

  // Find the best-window row index (by matching date string to tiers).
  const bestRowIndex =
    bestTier !== null
      ? tiers.findIndex((t) => t.date === bestTier.date)
      : -1;

  return (
    <div className="flex flex-col gap-4">
      {/* Back link — "← Dashboard" */}
      <Link
        to="/"
        className="field-label text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors flex items-center gap-1"
        data-testid="forecast-back-link"
      >
        &#8592; Dashboard
      </Link>

      {/* Header */}
      <div className="flex flex-col gap-3">
        <h1 className="font-display text-display-lg text-[var(--color-text-primary)]">
          8-Day Forecast
        </h1>
        {property && (
          <p className="text-body-sm text-[var(--color-text-muted)]">{property.name}</p>
        )}

        {/* Property selector — hide if only 1 property */}
        {properties.length >= 2 && (
          <SegmentedControl
            options={segmentedOptions}
            value={selectedId}
            onChange={(id) => {
              setSelectedId(id);
              setRawWeather(null);
              setExpandedIndex(0);
            }}
          />
        )}
      </div>

      {/* Loading skeleton */}
      {isLoading && !rawWeather && (
        <div
          className="bg-[var(--color-surface)] border-[0.5px] border-[var(--color-border)] shadow-card rounded-lg p-4 animate-pulse"
          data-testid="forecast-skeleton"
        >
          <div className="flex flex-col gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-12 bg-[var(--color-border)] rounded" />
            ))}
          </div>
        </div>
      )}

      {/* Empty / offline state — tiers is [] */}
      {!isLoading && tiers.length === 0 && (
        <div
          className="bg-[var(--color-surface)] border-[0.5px] border-[var(--color-border)] shadow-card rounded-lg p-6 text-center"
          data-testid="forecast-unavailable"
        >
          <p className="field-label text-[var(--color-text-muted)]">Forecast unavailable</p>
          <p className="text-body-sm text-[var(--color-text-muted)] mt-1">
            Conditions data hasn&#39;t synced for this location yet.
          </p>
        </div>
      )}

      {/* Best-window card — shown when ≥1 day */}
      {bestTier !== null && bestRowIndex >= 0 && rows[bestRowIndex] !== undefined && (
        <button
          type="button"
          className="w-full text-left bg-[var(--color-surface)] border-[0.5px] border-[var(--color-border)] shadow-card rounded-lg p-4 flex items-center gap-3 hover:bg-[var(--color-elevated)] transition-colors"
          onClick={() => handleBestWindowTap(bestRowIndex)}
          data-testid="forecast-best-window-card"
        >
          {/* Meter for the best day */}
          <ConditionMeter
            tier={(() => {
              const lvl = bestTier.tier;
              const map: Record<QualityLevel, 1 | 2 | 3 | 4> = {
                poor: 1, fair: 2, good: 3, excellent: 4,
              };
              return map[lvl];
            })()}
            colorToken={CONDITION_RATING_DISPLAY[bestTier.tier].token}
          />
          {/* Best window info */}
          <div className="flex-1 min-w-0">
            <p className="field-label text-[var(--color-text-muted)]">
              Best conditions this week
            </p>
            <p className="text-body-sm font-semibold text-[var(--color-text-primary)]">
              {/* CAST: rows[bestRowIndex] guard above */}
              {rows[bestRowIndex]!.dayLabel}
            </p>
            <p className="text-body-xs text-[var(--color-text-muted)]">
              {/* CAST: rows[bestRowIndex] guard above */}
              {Math.round(rows[bestRowIndex]!.tempMax)}° / {Math.round(rows[bestRowIndex]!.tempMin)}° &middot;{' '}
              {rows[bestRowIndex]!.windDir} {Math.round(rows[bestRowIndex]!.windSpeed)} &middot;{' '}
              {rows[bestRowIndex]!.precipPct}% precip
            </p>
          </div>
          <span className="text-[var(--color-text-muted)] text-sm" aria-hidden="true">›</span>
        </button>
      )}

      {/* Day rows */}
      {rows.length > 0 && (
        <div
          className="bg-[var(--color-surface)] border-[0.5px] border-[var(--color-border)] shadow-card rounded-lg overflow-hidden"
          data-testid="forecast-list"
        >
          {rows.map((row, index) => (
            <div
              key={tiers[index]!.date}
              ref={(el) => {
                rowRefs.current[index] = el;
              }}
            >
              <ForecastDayRow
                index={index}
                dayLabel={row.dayLabel}
                level={row.level}
                score={row.score}
                isNear={row.isNear}
                tempMax={row.tempMax}
                tempMin={row.tempMin}
                precipPct={row.precipPct}
                windSpeed={row.windSpeed}
                windDir={row.windDir}
                moonIllumination={row.moonIllumination}
                moonPhaseValue={row.moonPhaseValue}
                civilDawnTime={row.civilDawnTime}
                civilDuskTime={row.civilDuskTime}
                pressureInHg={row.pressureInHg}
                pressureTrend={row.pressureTrend}
                precipitationVolume={row.precipitationVolume}
                sparklinePoints={row.sparklinePoints}
                isExpanded={expandedIndex === index}
                onToggle={() => handleToggle(index)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
